import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="searchable-select"
// A searchable dropdown that fetches options from the server as you type
// Supports both single-select and multi-select modes
//
// Required data attributes:
//   data-searchable-select-url-value: URL to fetch options from (receives ?q= param)
//
// Optional data attributes:
//   data-searchable-select-storage-key-value: localStorage key for recent items
//   data-searchable-select-max-recent-value: Max recently used items (default: 5)
//   data-searchable-select-min-chars-value: Min chars before searching (default: 0)
//   data-searchable-select-debounce-value: Debounce delay in ms (default: 300)
//   data-searchable-select-multiple-value: Enable multi-select mode (default: false)
//   data-searchable-select-selected-items-value: Initial selected items for multi-select (JSON array)
//
export default class extends Controller {
  static targets = [
    "input",           // Hidden input for form value (single-select)
    "inputsContainer", // Container for hidden inputs (multi-select)
    "trigger",         // Clickable trigger element
    "label",           // Label text element (single-select)
    "tagsContainer",   // Container for selected tags (multi-select)
    "placeholderText", // Placeholder text element (multi-select)
    "dropdown",        // Dropdown container
    "search",          // Search input
    "blankOption",     // None/Clear option
    "recentSection",   // Recent items section container
    "recentList",      // Recent items list
    "optionsList",     // Options list (populated dynamically)
    "loading",         // Loading indicator
    "empty",           // Empty state message
    "formButton"       // Action buttons — add-new / edit-selected (optional, 0+)
  ]

  static values = {
    url: String,                                    // Required: URL to fetch options
    storageKey: { type: String, default: "" },      // localStorage key for recent items
    maxRecent: { type: Number, default: 5 },        // Max recently used items
    minChars: { type: Number, default: 0 },         // Min chars before searching
    debounce: { type: Number, default: 300 },       // Debounce delay in ms
    placeholder: { type: String, default: "Select..." },
    searchPlaceholder: { type: String, default: "Type to search..." },
    multiple: { type: Boolean, default: false },    // Multi-select mode
    selectedItems: { type: Array, default: [] },    // Initial selected items [{value, label}]
    fieldName: { type: String, default: "" },       // Form field name
    embedded: { type: Boolean, default: false },    // Embedded mode (no popover, auto-open)
    allowCustom: { type: Boolean, default: false },  // Allow free-text entry not in the list
    forms: { type: Array, default: [] },            // Form button configs [{label, modalId, url?, urlTemplate?}]
    initialValue: { type: String, default: "" },    // Initial hidden input value (embedded self-render)
    initialSearch: { type: String, default: "" }    // Initial search text (embedded self-render)
  }

  connect() {
    this.isOpen = false
    this.focusedIndex = -1
    this.options = []
    this.abortController = null

    // In embedded mode, self-render the dropdown structure if not already present
    if (this.embeddedValue && !this.hasDropdownTarget) {
      this.#renderEmbeddedDropdown()
    }

    // Initialize selected items for multi-select
    if (this.multipleValue) {
      this.selectedItems = [...this.selectedItemsValue]
    }

    this.loadRecentItems()
    this.#updateEditButton()

    // Sync isOpen when browser light-dismisses the popover (click outside or Escape)
    if (!this.embeddedValue) {
      this.boundHandlePopoverToggle = this.handlePopoverToggle.bind(this)
      this.dropdownTarget.addEventListener("toggle", this.boundHandlePopoverToggle)
    }

    // Listen for newly created records (from slideouts/modals)
    this.boundHandleRecordCreated = this.handleRecordCreated.bind(this)
    document.addEventListener("record-created", this.boundHandleRecordCreated)

    // In embedded mode (e.g. AG Grid cell editor), auto-open immediately
    if (this.embeddedValue) {
      this.open()
    }
  }

  disconnect() {
    if (this.boundHandlePopoverToggle && this.hasDropdownTarget) {
      this.dropdownTarget.removeEventListener("toggle", this.boundHandlePopoverToggle)
    }
    document.removeEventListener("record-created", this.boundHandleRecordCreated)
    this.cancelPendingRequest()

    // Remove from add-new stack if this instance is destroyed mid-flow
    const stack = this.constructor._addNewRequesters
    if (stack) {
      const index = stack.lastIndexOf(this)
      if (index !== -1) stack.splice(index, 1)
    }
  }

  // Handle record-created events from slideouts/modals.
  // Only the instance that initiated "Add New" gets the value selected;
  // all others just update their recent-items cache.
  handleRecordCreated(event) {
    const { storageKey, value, label } = event.detail
    if (!storageKey || storageKey !== this.storageKeyValue) return

    const stack = this.constructor._addNewRequesters || []
    const index = stack.lastIndexOf(this)
    if (index !== -1) {
      stack.splice(index, 1)
      if (this.multipleValue) {
        this.addValue(value, label)
      } else {
        this.setValue(value, label)
      }
    } else if (!this.multipleValue && this.hasInputTarget && String(this.inputTarget.value) === String(value)) {
      // The currently-selected record was edited — refresh the displayed label
      this.setValue(value, label)
    }
    this.addToRecent(value, label)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.isOpen = true

    if (!this.embeddedValue) {
      this.dropdownTarget.showPopover()
    }

    this.focusedIndex = -1
    this.renderRecentItems()
    this.clearOptions()

    // Focus search input
    requestAnimationFrame(() => {
      if (this.hasSearchTarget) {
        this.searchTarget.focus()
        // In embedded mode, preserve the pre-populated value (e.g. current cell value)
        if (!this.embeddedValue) {
          this.searchTarget.value = ""
        }
      }
    })

    // If minChars is 0, fetch initial results
    if (this.minCharsValue === 0) {
      this.fetchOptions("")
    }
  }

  close() {
    this.isOpen = false

    if (!this.embeddedValue) {
      try { this.dropdownTarget.hidePopover() } catch { /* already hidden */ }
    }

    this.focusedIndex = -1
    this.cancelPendingRequest()

    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }

    // Notify parent (e.g. AG Grid cell editor) that the dropdown was dismissed
    if (this.embeddedValue) {
      this.element.dispatchEvent(new CustomEvent("searchable-select:dismiss", { bubbles: true }))
    }
  }

  // Sync state when the browser dismisses the popover (light dismiss or Escape)
  handlePopoverToggle(event) {
    if (event.newState === "closed" && this.isOpen) {
      this.isOpen = false
      this.focusedIndex = -1
      this.cancelPendingRequest()
      if (this.hasSearchTarget) this.searchTarget.value = ""
    }
  }

  // Called on search input
  search() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim() : ""

    // Hide recent items and blank option when searching, show when input is empty
    if (query.length > 0) {
      if (this.hasRecentSectionTarget) this.recentSectionTarget.classList.add("hidden")
      if (this.hasBlankOptionTarget) this.blankOptionTarget.classList.add("hidden")
    } else {
      this.renderRecentItems()
      if (this.hasBlankOptionTarget) this.blankOptionTarget.classList.remove("hidden")
    }

    // Clear pending debounce
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    // Check minimum characters
    if (query.length < this.minCharsValue) {
      this.clearOptions()
      if (this.hasEmptyTarget) {
        this.emptyTarget.textContent = `Type at least ${this.minCharsValue} characters...`
        this.emptyTarget.classList.remove("hidden")
      }
      return
    }

    // Debounce the fetch
    this.debounceTimeout = setTimeout(() => {
      this.fetchOptions(query)
    }, this.debounceValue)
  }

  async fetchOptions(query) {
    // Cancel any pending request
    this.cancelPendingRequest()

    // Show loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden")
    }

    try {
      this.abortController = new AbortController()

      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error("Network response was not ok")

      const data = await response.json()
      this.options = data.results || data
      this.renderOptions()

    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching options:", error)
        this.clearOptions()
        if (this.hasEmptyTarget) {
          this.emptyTarget.textContent = "Error loading options"
          this.emptyTarget.classList.remove("hidden")
        }
      }
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
      this.abortController = null
    }
  }

  cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }
  }

  renderOptions() {
    if (!this.hasOptionsListTarget) return

    this.optionsListTarget.innerHTML = ""

    if (this.options.length === 0) {
      if (this.allowCustomValue) {
        if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
        this.renderCustomOption()
        return
      }
      if (this.hasEmptyTarget) {
        this.emptyTarget.textContent = "No results found"
        this.emptyTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden")
    }

    let currentGroup = null

    this.options.forEach(option => {
      // Render group header if this option starts a new group
      if (option.group && option.group !== currentGroup) {
        currentGroup = option.group
        const header = document.createElement("li")
        header.className = "px-2 pt-3 pb-1 text-xs font-semibold text-base-content/50 uppercase tracking-wide"
        header.textContent = option.group
        this.optionsListTarget.appendChild(header)
      }

      const li = document.createElement("li")
      const a = document.createElement("a")
      a.className = "block px-2 py-1.5 cursor-pointer hover:bg-base-200 rounded flex items-center gap-2"
      a.dataset.action = "click->searchable-select#select"
      a.dataset.value = option.value
      a.dataset.label = option.label

      // Check if this option is already selected (for multi-select)
      const isSelected = this.multipleValue && this.isValueSelected(option.value)

      // Build the option content
      if (this.multipleValue) {
        // Multi-select: show checkbox indicator
        const checkbox = document.createElement("span")
        checkbox.className = `w-4 h-4 flex items-center justify-center rounded border ${isSelected ? 'bg-primary border-primary text-primary-content' : 'border-base-300'}`
        if (isSelected) {
          checkbox.innerHTML = `<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>`
        }

        const text = document.createElement("span")
        text.className = "flex-1"

        if (option.subtitle) {
          text.innerHTML = `
            <div>${option.label}</div>
            <div class="text-xs text-secondary">${option.subtitle}</div>
          `
        } else {
          text.textContent = option.label
        }

        a.appendChild(checkbox)
        a.appendChild(text)
      } else {
        // Single-select: just show the label
        if (option.subtitle) {
          a.innerHTML = `
            <div>${option.label}</div>
            <div class="text-xs text-secondary">${option.subtitle}</div>
          `
        } else {
          a.textContent = option.label
        }
      }

      li.appendChild(a)
      this.optionsListTarget.appendChild(li)
    })

    this.renderCustomOption()
  }

  renderCustomOption() {
    if (!this.allowCustomValue || !this.hasOptionsListTarget) return

    const query = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    if (!query) return

    // Don't show if an exact match already exists
    const exactMatch = this.options.some(o => o.label.toLowerCase() === query.toLowerCase())
    if (exactMatch) return

    const li = document.createElement("li")
    const a = document.createElement("a")
    a.className = "block px-2 py-1.5 cursor-pointer hover:bg-base-200 rounded text-primary"
    a.dataset.action = "click->searchable-select#select"
    a.dataset.value = query
    a.dataset.label = query
    a.textContent = `Use "${query}"`
    li.appendChild(a)
    this.optionsListTarget.appendChild(li)
  }

  clearOptions() {
    if (this.hasOptionsListTarget) {
      this.optionsListTarget.innerHTML = ""
    }
    this.options = []
  }

  select(event) {
    const target = event.currentTarget
    const value = target.dataset.value
    const label = target.dataset.label

    if (this.multipleValue) {
      // Multi-select: toggle the value
      this.toggleValue(value, label)
      // Re-render to update checkboxes
      this.renderRecentItems()
      this.renderOptions()
      // Don't close dropdown in multi-select mode
    } else {
      // Single-select: set and close
      this.setValue(value, label)
      this.addToRecent(value, label)
      this.close()
    }
  }

  // Select "None" option (single) or "Clear all" (multi)
  selectNone(event) {
    event.preventDefault()

    if (this.multipleValue) {
      // Clear all selections
      this.selectedItems = []
      this.syncHiddenInputs()
      this.renderTags()
      this.renderOptions() // Update checkboxes

      this.element.dispatchEvent(new CustomEvent("searchable-select:change", {
        detail: { values: [], items: [] },
        bubbles: true
      }))
    } else {
      this.setValue("", this.placeholderValue)
    }

    this.close()
  }

  // Single-select: set the value
  setValue(value, label, { silent = false } = {}) {
    if (this.hasInputTarget) {
      this.inputTarget.value = value
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = label || this.placeholderValue
      this.labelTarget.classList.toggle("text-base-content/50", !label)
    }
    this.#updateEditButton()
    if (!silent) {
      this.element.dispatchEvent(new CustomEvent("searchable-select:change", {
        detail: { value, label },
        bubbles: true
      }))
    }
  }

  // Multi-select: check if value is selected
  isValueSelected(value) {
    return this.selectedItems.some(item => String(item.value) === String(value))
  }

  // Multi-select: toggle a value
  toggleValue(value, label) {
    if (this.isValueSelected(value)) {
      this.removeValue(value)
    } else {
      this.addValue(value, label)
    }
  }

  // Multi-select: add a value to the selection
  addValue(value, label, { silent = false } = {}) {
    if (this.isValueSelected(value)) return // Already selected

    this.selectedItems.push({ value: String(value), label })
    this.syncHiddenInputs()
    this.renderTags()
    this.addToRecent(value, label)
    if (!silent) {
      this.element.dispatchEvent(new CustomEvent("searchable-select:change", {
        detail: { values: this.selectedItems.map(i => i.value), items: this.selectedItems },
        bubbles: true
      }))
    }
  }

  // Multi-select: remove a value from the selection
  removeValue(value, { silent = false } = {}) {
    this.selectedItems = this.selectedItems.filter(item => String(item.value) !== String(value))
    this.syncHiddenInputs()
    this.renderTags()
    if (!silent) {
      this.element.dispatchEvent(new CustomEvent("searchable-select:change", {
        detail: { values: this.selectedItems.map(i => i.value), items: this.selectedItems },
        bubbles: true
      }))
    }
  }

  // Multi-select: remove tag when X is clicked
  removeTag(event) {
    const value = event.currentTarget.dataset.value
    this.removeValue(value)

    // Re-render options if dropdown is open
    if (this.isOpen) {
      this.renderOptions()
    }
  }

  // Multi-select: sync hidden inputs with selected items
  syncHiddenInputs() {
    if (!this.hasInputsContainerTarget) return

    // Clear existing inputs
    this.inputsContainerTarget.innerHTML = ""

    // Create a hidden input for each selected item
    this.selectedItems.forEach(item => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.fieldNameValue
      input.value = item.value
      this.inputsContainerTarget.appendChild(input)
    })
  }

  // Multi-select: render the selected tags in the trigger
  renderTags() {
    if (!this.hasTagsContainerTarget) return

    this.tagsContainerTarget.innerHTML = ""

    if (this.selectedItems.length === 0) {
      // Show placeholder
      const placeholder = document.createElement("span")
      placeholder.className = "text-base-content/50"
      placeholder.dataset.searchableSelectTarget = "placeholderText"
      placeholder.textContent = this.placeholderValue
      this.tagsContainerTarget.appendChild(placeholder)
      return
    }

    // Render tags
    this.selectedItems.forEach(item => {
      const tag = document.createElement("span")
      tag.className = "badge badge-neutral gap-1"
      tag.dataset.value = item.value
      tag.innerHTML = `
        ${this.escapeHtml(item.label)}
        <button type="button"
                class="btn btn-ghost btn-xs p-0 h-auto min-h-0"
                data-action="click->searchable-select#removeTag:prevent:stop"
                data-value="${item.value}">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      `
      this.tagsContainerTarget.appendChild(tag)
    })
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Keyboard navigation
  handleTriggerKeydown(event) {
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault()
      this.open()
    }
  }

  handleSearchKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveFocus(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveFocus(-1)
        break
      case "Enter":
        event.preventDefault()
        if (this.focusedIndex >= 0) {
          this.selectFocused()
        } else if (this.allowCustomValue) {
          const customValue = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
          if (customValue) {
            this.setValue(customValue, customValue)
            this.addToRecent(customValue, customValue)
            this.close()
          }
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        if (this.hasTriggerTarget) {
          this.triggerTarget.focus()
        }
        break
    }
  }

  moveFocus(direction) {
    const options = this.getVisibleOptions()
    if (options.length === 0) return

    this.focusedIndex += direction

    if (this.focusedIndex < 0) {
      this.focusedIndex = options.length - 1
    } else if (this.focusedIndex >= options.length) {
      this.focusedIndex = 0
    }

    this.updateFocusedState(options)
  }

  getVisibleOptions() {
    const allOptions = []

    // Add recent items first
    if (this.hasRecentListTarget) {
      this.recentListTarget.querySelectorAll("[data-value]").forEach(opt => {
        allOptions.push(opt)
      })
    }

    // Add fetched options
    if (this.hasOptionsListTarget) {
      this.optionsListTarget.querySelectorAll("[data-value]").forEach(opt => {
        allOptions.push(opt)
      })
    }

    return allOptions
  }

  updateFocusedState(options) {
    options.forEach(opt => opt.classList.remove("bg-base-200"))

    if (this.focusedIndex >= 0 && this.focusedIndex < options.length) {
      const focused = options[this.focusedIndex]
      focused.classList.add("bg-base-200")
      focused.scrollIntoView({ block: "nearest" })
    }
  }

  selectFocused() {
    const options = this.getVisibleOptions()
    if (this.focusedIndex >= 0 && this.focusedIndex < options.length) {
      const option = options[this.focusedIndex]
      this.select({ currentTarget: option })
    }
  }

  // Recently used items (localStorage)
  loadRecentItems() {
    if (!this.storageKeyValue) {
      this.recentItems = []
      return
    }

    try {
      const stored = localStorage.getItem(`searchable_select_recent_${this.storageKeyValue}`)
      this.recentItems = stored ? JSON.parse(stored) : []
    } catch {
      this.recentItems = []
    }
  }

  saveRecentItems() {
    if (!this.storageKeyValue) return

    try {
      localStorage.setItem(
        `searchable_select_recent_${this.storageKeyValue}`,
        JSON.stringify(this.recentItems.slice(0, this.maxRecentValue))
      )
    } catch {
      // localStorage might be full or disabled
    }
  }

  addToRecent(value, label) {
    if (!this.storageKeyValue || !value) return

    // Remove if already exists
    this.recentItems = this.recentItems.filter(item => item.value !== value)

    // Add to beginning
    this.recentItems.unshift({ value, label })

    // Trim to max
    this.recentItems = this.recentItems.slice(0, this.maxRecentValue)

    this.saveRecentItems()
  }

  renderRecentItems() {
    if (!this.hasRecentListTarget || !this.hasRecentSectionTarget) return

    if (this.recentItems.length === 0) {
      this.recentSectionTarget.classList.add("hidden")
      return
    }

    this.recentSectionTarget.classList.remove("hidden")
    this.recentListTarget.innerHTML = ""

    this.recentItems.forEach(item => {
      const li = document.createElement("li")
      const a = document.createElement("a")
      a.className = "block px-2 py-1.5 cursor-pointer hover:bg-base-200 rounded flex items-center gap-2"
      a.dataset.value = item.value
      a.dataset.label = item.label

      if (this.multipleValue) {
        // Show checkbox for multi-select
        const isSelected = this.isValueSelected(item.value)
        const checkbox = document.createElement("span")
        checkbox.className = `w-4 h-4 flex items-center justify-center rounded border ${isSelected ? 'bg-primary border-primary text-primary-content' : 'border-base-300'}`
        if (isSelected) {
          checkbox.innerHTML = `<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>`
        }
        a.appendChild(checkbox)

        const text = document.createElement("span")
        text.textContent = item.label
        a.appendChild(text)
      } else {
        a.textContent = item.label
      }

      a.addEventListener("click", (e) => this.select(e))
      li.appendChild(a)
      this.recentListTarget.appendChild(li)
    })
  }

  // Unified action for add-new and edit-selected buttons.
  // Reads data attributes from the clicked button to decide which flow to run:
  //   - url-template present → edit flow (load turbo-frame src)
  //   - url-template absent  → add-new flow (push to requester stack + dispatch event)
  openForm(event) {
    event.preventDefault()
    // In embedded mode (e.g. AG Grid), the cell editor handles the lifecycle —
    // don't dismiss, as that would destroy the element before the modal opens.
    if (!this.embeddedValue) {
      this.close()
    }

    if (!this.constructor._addNewRequesters) this.constructor._addNewRequesters = []
    this.constructor._addNewRequesters.push(this)

    this.element.dispatchEvent(new CustomEvent("searchable-select:add-new", {
      bubbles: true
    }))
  }

  // Show/hide edit buttons and keep their data-base-url in sync with the selected value.
  #updateEditButton() {
    const value = this.hasInputTarget ? this.inputTarget.value : ""
    this.formButtonTargets.forEach(btn => {
      const urlTemplate = btn.dataset.urlTemplate
      if (!urlTemplate) return

      btn.classList.toggle("hidden", !value)
      if (value) {
        btn.dataset.baseUrl = urlTemplate.replace(":id", value)
      }
    })
  }

  // Public method to set value programmatically without triggering change events.
  setValueExternally(value, label) {
    if (this.multipleValue) {
      this.addValue(value, label, { silent: true })
    } else {
      this.setValue(value, label, { silent: true })
      this.addToRecent(value, label)
    }
  }

  // ── Self-rendering (embedded mode) ──────────────────────────

  // Build the full dropdown DOM when running in embedded mode (e.g. AG Grid).
  // The cell editor only needs to create the root element with data attributes;
  // the controller handles all internal markup.
  #renderEmbeddedDropdown() {
    let formsHtml = ""
    if (this.formsValue.length > 0) {
      const buttons = this.formsValue.map(form => {
        if (form.urlTemplate) {
          return `
            <button type="button"
                    class="btn btn-ghost btn-xs text-base-content/40 hover:text-base-content hidden"
                    data-controller="modal"
                    data-searchable-select-target="formButton"
                    data-action="click->searchable-select#openForm click->modal#open"
                    data-modal-target-param="${this.#escapeAttr(form.modalId)}"
                    data-url-template="${this.#escapeAttr(form.urlTemplate)}">
              ${this.escapeHtml(form.label)}
            </button>`
        } else {
          return `
            <button type="button"
                    class="btn btn-ghost btn-sm justify-start text-primary"
                    data-controller="modal"
                    data-searchable-select-target="formButton"
                    data-action="click->searchable-select#openForm click->modal#open"
                    data-modal-target-param="${this.#escapeAttr(form.modalId)}"
                    ${form.url ? `data-base-url="${this.#escapeAttr(form.url)}"` : ""}>
              ${this.escapeHtml(form.label)}
            </button>`
        }
      }).join("")

      formsHtml = `
        <div class="px-3 py-2 border-t border-base-300 flex items-center justify-between">
          ${buttons}
        </div>`
    }

    this.element.innerHTML = `
      <input type="hidden" data-searchable-select-target="input" value="${this.#escapeAttr(this.initialValueValue)}">
      <div data-searchable-select-target="dropdown"
           class="bg-base-100 shadow-xl rounded-box border border-base-300 overflow-hidden">
        <div class="p-2 border-b border-base-300">
          <input type="text"
                 placeholder="Search..."
                 class="input input-bordered input-sm w-full"
                 data-searchable-select-target="search"
                 data-action="input->searchable-select#search keydown->searchable-select#handleSearchKeydown"
                 autocomplete="off"
                 value="${this.#escapeAttr(this.initialSearchValue)}">
        </div>
        <div class="max-h-60 overflow-y-auto overscroll-contain">
          <div class="p-2 border-b border-base-300 hidden" data-searchable-select-target="recentSection">
            <p class="text-xs text-secondary px-2 py-1">Recently Used</p>
            <ul data-searchable-select-target="recentList"></ul>
          </div>
          <div class="p-4 text-center hidden" data-searchable-select-target="loading">
            <span class="loading loading-spinner loading-sm"></span>
            <span class="text-sm text-secondary ml-2">Searching...</span>
          </div>
          <div class="p-4 text-center text-secondary text-sm hidden" data-searchable-select-target="empty">
            No results found
          </div>
          <ul class="p-2" data-searchable-select-target="optionsList"></ul>
        </div>
        ${formsHtml}
      </div>
    `
  }

  #escapeAttr(str) {
    if (!str) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
